import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Refresh as RefreshIcon,
  BugReport as BugReportIcon,
  MenuBook as MenuBookIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '../../../stores/uiStore';
import { clearAllStorageData } from '../../../utils/storageVersion';
import { useAuthStore } from '../../authentication/store/authStore';
import { usePostHog } from '../../../hooks/usePostHog';
import { getDocsUrl } from '../../../utils/docsUrl';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentTheme, setTheme } = useUIStore();
  const { user, getCurrentRoles } = useAuthStore();
  const { captureException, addBreadcrumb } = usePostHog();
  const [showClearCacheDialog, setShowClearCacheDialog] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false);

  const handleThemeChange = () => {
    setTheme(currentTheme === 'light' ? 'dark' : 'light');
  };

  const handleClearCache = () => {
    try {
      clearAllStorageData();
      setCacheCleared(true);
      setShowClearCacheDialog(false);

      // Redirect to login after a short delay
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  };

  // Test error tracking functions (TEMPORARY - FOR TESTING ONLY)
  const testErrorBoundary = () => {
    addBreadcrumb('User clicked test error button', 'user', { action: 'test_error' });

    // Enhanced error with stack trace info
    const testError = new Error('Test error from Settings page - Error Boundary');
    testError.stack = `Error: Test error from Settings page - Error Boundary
    at testErrorBoundary (SettingsPage.tsx:${new Error().stack?.split('\n')[1]?.match(/:(\d+):/)?.[1] || '71'})
    at onClick (SettingsPage.tsx:236)
    at HTMLButtonElement.onClick`;

    throw testError;
  };

  const testManualError = () => {
    const error = new Error('Test manual error capture from Settings page');

    captureException(error, {
      page: 'settings',
      test: true,
      manual_capture: true,
      user_id: user?.id,
      source_context: 'settings_page_manual_test',
    });

    alert('Manual error captured! Check PostHog dashboard.');
  };

  const testUnhandledPromiseRejection = () => {
    addBreadcrumb('User triggered promise rejection test', 'user', { action: 'test_promise' });
    Promise.reject(new Error('Test unhandled promise rejection from Settings page'));
    alert('Unhandled promise rejection triggered! Check PostHog dashboard.');
  };

  const userRoles = getCurrentRoles();
  const isProfessor =
    userRoles.includes('PROFESSOR') || userRoles.includes('COURSE_REPRESENTATIVE');

  const handleReportBug = () => {
    navigate('/app/bug-report');
  };

  const handleOpenDocs = () => {
    window.open(getDocsUrl(), '_blank', 'noopener,noreferrer');
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
              border: '1px solid',
              borderColor: 'divider',
            })}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Appearance
              </Typography>

              <Paper
                variant="outlined"
                sx={(theme) => ({
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor:
                    theme.palette.mode === 'light' ? 'background.default' : theme.palette.grey[900],
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {currentTheme === 'light' ? (
                    <LightModeIcon color="primary" />
                  ) : (
                    <DarkModeIcon color="primary" />
                  )}
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500}>
                      Dark Mode
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentTheme === 'light'
                        ? 'Switch to dark theme for reduced eye strain'
                        : 'Dark mode is currently enabled'}
                    </Typography>
                  </Box>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={currentTheme === 'dark'}
                      onChange={handleThemeChange}
                      color="primary"
                    />
                  }
                  label=""
                />
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
              border: '1px solid',
              borderColor: 'divider',
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BugReportIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6">Report a Bug</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Found an issue or bug in Lecturo? We appreciate your feedback to help us improve the
                platform.
              </Typography>

              <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
                When to report a bug:
              </Typography>

              <List dense sx={{ mb: 2 }}>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        Something isn&apos;t working as expected
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2">You see an error message</Typography>}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        A feature is not functioning correctly
                      </Typography>
                    }
                  />
                </ListItem>
              </List>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<BugReportIcon />}
                  onClick={handleReportBug}
                  sx={{ flexGrow: { xs: 1, sm: 0 } }}
                >
                  Report Bug
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<MenuBookIcon />}
                  onClick={handleOpenDocs}
                  sx={{ flexGrow: { xs: 1, sm: 0 } }}
                >
                  User Guide
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {isProfessor && (
          <Grid item xs={12}>
            <Card
              sx={(theme) => ({
                backgroundColor:
                  theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
                border: '1px solid',
                borderColor: 'divider',
              })}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BugReportIcon sx={{ mr: 2, color: 'warning.main' }} />
                  <Typography variant="h6">Troubleshooting</Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  If you&apos;re experiencing issues like &quot;You have no subjects&quot; errors or
                  login problems after system updates, clearing your browser cache may resolve the
                  issue.
                </Typography>

                <Paper
                  variant="outlined"
                  sx={(theme) => ({
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor:
                      theme.palette.mode === 'light'
                        ? 'background.default'
                        : theme.palette.grey[900],
                  })}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 0.5 }}>
                      Clear Application Cache
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Resolves login issues and outdated data problems. You&apos;ll need to log in
                      again.
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RefreshIcon />}
                    onClick={() => setShowClearCacheDialog(true)}
                    sx={{ ml: 2 }}
                  >
                    Clear Cache
                  </Button>
                </Paper>

                {cacheCleared && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Cache cleared successfully! Redirecting to login...
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* PostHog Error Tracking Test Section - System Admin Only */}
        {userRoles.includes('SYSTEM_ADMIN') && (
          <Grid item xs={12}>
            <Card sx={{ border: '2px solid orange', backgroundColor: '#fff3cd' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BugReportIcon sx={{ mr: 2, color: 'warning.main' }} />
                  <Typography variant="h6" color="warning.main">
                    🧪 PostHog Error Tracking Test (System Admin)
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Test buttons for PostHog error tracking integration. Only visible to system
                  administrators.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={testErrorBoundary}
                      size="small"
                      fullWidth
                    >
                      Test Error Boundary
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={testManualError}
                      size="small"
                      fullWidth
                    >
                      Test Manual Capture
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={testUnhandledPromiseRejection}
                      size="small"
                      fullWidth
                    >
                      Test Promise Rejection
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="outlined"
                      color="info"
                      onClick={() => {
                        const stack = new Error('Test stack trace').stack;
                        alert(`Stack trace captured:\n${stack}`);
                      }}
                      size="small"
                      fullWidth
                    >
                      Analyze Stack
                    </Button>
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    These errors will be captured by PostHog with automatic context injection
                    (tenant, domain, user info). Check your PostHog Error Tracking dashboard.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Clear Cache Confirmation Dialog */}
      <Dialog open={showClearCacheDialog} onClose={() => setShowClearCacheDialog(false)}>
        <DialogTitle>Clear Application Cache?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This will clear all cached data including:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>Login session data</li>
            <li>Subject and course information</li>
            <li>Navigation context</li>
            <li>User preferences (except theme)</li>
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>You&apos;ll need to log in again</strong> after clearing the cache. This
              action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClearCacheDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleClearCache} color="warning" variant="contained">
            Clear Cache
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;
